import {ContentContainer} from '@coolcolduk/ui-mui';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import PageHeader from '../../../components/PageHeader';
import {PrivateRouteProps} from '../../../config/routeType';
import DropdownModule from '../../../atoms/dropdownModule/DropdownModule';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useSeedQuestionTopicCounts} from '../../../hooks/seedQuestion/useSeedQuestionTopicCounts';

export default function AdminSeedQuestionOrphanPage(_props: PrivateRouteProps) {
  const {examModules, loading: modulesLoading} = useListExamModules();
  const [moduleId, setModuleId] = useState<string>('');
  const {topicCounts, loading: countsLoading, error, refetch} = useSeedQuestionTopicCounts(moduleId || undefined);

  const selectedModule = examModules.find((m) => m.id === moduleId);
  const moduleTopicSet = new Set(selectedModule?.topics?.map((t) => t.topic) ?? []);

  const rowsFromModule = (selectedModule?.topics ?? []).map((t) => ({
    topic: t.topic,
    count: topicCounts.find((tc) => tc.topic === t.topic)?.count ?? 0,
    isOrphan: false,
  }));

  const orphanRows = topicCounts
    .filter((tc) => !moduleTopicSet.has(tc.topic))
    .map((tc) => ({
      topic: tc.topic,
      count: tc.count,
      isOrphan: true,
    }));

  const totalSeedQuestions = topicCounts.reduce((sum, tc) => sum + tc.count, 0);

  return (
    <ContentContainer data-testid="admin-seed-question-orphan-page">
      <PageHeader title="Seed Question Orphan" />

      <Stack spacing={3} sx={{mt: 2}}>
        <DropdownModule
          examModules={examModules || []}
          moduleId={moduleId}
          onChange={setModuleId}
          disabled={modulesLoading}
          autoSelectFirst={false}
        />

        {moduleId && (
          <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Button
                variant="outlined"
                startIcon={countsLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
                onClick={() => refetch()}
                disabled={countsLoading}
              >
                Refresh counts
              </Button>
              {totalSeedQuestions > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Total seed questions: {totalSeedQuestions}
                </Typography>
              )}
            </Box>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            {countsLoading && topicCounts.length === 0 ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{color: '#1B263B'}}>
                  Topics (module) and seed question count
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Topic</TableCell>
                        <TableCell align="right">Seed questions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsFromModule.map((row) => (
                        <TableRow key={row.topic}>
                          <TableCell>{row.topic}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {orphanRows.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{color: '#1B263B'}}>
                      Orphan topics (in seed questions but not in module)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Topic</TableCell>
                            <TableCell align="right">Seed questions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orphanRows.map((row) => (
                            <TableRow key={row.topic}>
                              <TableCell>{row.topic}</TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </ContentContainer>
  );
}
